import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  hospitalAdminApi,
  type AgencyApiKeyRow,
  type AgencyUserOption,
  type RoundRobinAssignee,
  type RoundRobinAssigneePayload,
  type RoundRobinConfig,
  type HospitalWhatsAppNumber,
  type WhatsAppAutoReplyRule,
} from "@/api/hospitalAdmin";
import { toast } from "sonner";
import { CRM_LEAD_SOURCES, formatLeadSourceLabel } from "@/lib/ipdLeadSources";

type CreateHospitalWhatsAppPayload = Parameters<typeof hospitalAdminApi.createHospitalWhatsAppNumber>[0];
type UpdateHospitalWhatsAppPayload = Parameters<typeof hospitalAdminApi.updateHospitalWhatsAppNumber>[1];
import { Copy, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function MiniCrmSettings() {
  const qc = useQueryClient();
  const [keyName, setKeyName] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [editingKey, setEditingKey] = useState<AgencyApiKeyRow | null>(null);
  const [editName, setEditName] = useState("");
  const [editOwnerId, setEditOwnerId] = useState("");
  const [ruleName, setRuleName] = useState("");
  const [ruleKeywords, setRuleKeywords] = useState("");
  const [ruleReplyText, setRuleReplyText] = useState("");
  const [rulePriority, setRulePriority] = useState("100");
  const [waPhoneNumberId, setWaPhoneNumberId] = useState("");
  const [waAccessToken, setWaAccessToken] = useState("");
  const [sourcesDialogFor, setSourcesDialogFor] = useState<RoundRobinAssignee | null>(null);
  const [draftDeniedSources, setDraftDeniedSources] = useState<string[]>([]);
  const [waWabaId, setWaWabaId] = useState("");
  const [waName, setWaName] = useState("");
  const [waLeadIngestKeyId, setWaLeadIngestKeyId] = useState("");
  const [waIsDefault, setWaIsDefault] = useState(false);
  const [waIsActive, setWaIsActive] = useState(true);

  const { data, isLoading } = useQuery<RoundRobinConfig>({
    queryKey: ["lead-round-robin-config"],
    queryFn: () => hospitalAdminApi.getLeadRoundRobinConfig(),
  });

  const enabled = !!data?.leadRoundRobinEnabled;

  const { data: agencyUsers = [] } = useQuery<AgencyUserOption[]>({
    queryKey: ["lead-agency-users"],
    queryFn: () => hospitalAdminApi.listAgencyUsers(),
  });

  const { data: apiKeys = [], isLoading: loadingKeys } = useQuery<AgencyApiKeyRow[]>({
    queryKey: ["lead-agency-keys"],
    queryFn: () => hospitalAdminApi.listAgencyApiKeys(),
  });

  const { data: rrData } = useQuery<RoundRobinAssigneePayload>({
    queryKey: ["lead-round-robin-assignees"],
    queryFn: () => hospitalAdminApi.listRoundRobinAssignees(),
  });
  const rrAssignees = rrData?.assignees ?? [];
  const rrManagers = rrData?.managers ?? [];
  const { data: autoReplyRules = [], isLoading: loadingAutoReplyRules } = useQuery<WhatsAppAutoReplyRule[]>({
    queryKey: ["whatsapp-auto-reply-rules"],
    queryFn: () => hospitalAdminApi.listWhatsAppAutoReplyRules(),
  });

  const { data: hospitalWhatsAppNumbers = [], isLoading: loadingWhatsAppNumbers } = useQuery<HospitalWhatsAppNumber[]>({
    queryKey: ["hospital-whatsapp-numbers"],
    queryFn: () => hospitalAdminApi.listHospitalWhatsAppNumbers(),
  });

  const ownerOptions = useMemo(() => agencyUsers, [agencyUsers]);

  /** Include current key owner in the dropdown if they are inactive (not in agencyUsers). */
  const editOwnerOptions = useMemo(() => {
    if (!editingKey?.assignedToId) return ownerOptions;
    const has = ownerOptions.some((u) => u.id === editingKey.assignedToId);
    if (has) return ownerOptions;
    return [
      {
        id: editingKey.assignedToId,
        name: editingKey.assignedTo?.name ?? "Current owner",
        email: editingKey.assignedTo?.email ?? "",
        isLeadAgency: false,
      },
      ...ownerOptions,
    ];
  }, [ownerOptions, editingKey]);

  const setConfigMutation = useMutation({
    mutationFn: async (nextEnabled: boolean) => hospitalAdminApi.setLeadRoundRobinConfig(nextEnabled),
    onSuccess: async () => {
      toast.success("Round robin setting updated");
      await qc.invalidateQueries({ queryKey: ["lead-round-robin-config"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed to update"),
  });

  const setAssigneeMutation = useMutation({
    mutationFn: ({ staffId, isActive }: { staffId: string; isActive: boolean }) =>
      hospitalAdminApi.setRoundRobinAssigneeActive(staffId, isActive),
    onSuccess: async () => {
      toast.success("Salesperson availability updated");
      await qc.invalidateQueries({ queryKey: ["lead-round-robin-assignees"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed to update"),
  });

  const setAssigneeManagerMutation = useMutation({
    mutationFn: ({ staffId, managerId }: { staffId: string; managerId: string | null }) =>
      hospitalAdminApi.setRoundRobinAssigneeManager(staffId, managerId),
    onSuccess: async () => {
      toast.success("Reporting manager updated");
      await qc.invalidateQueries({ queryKey: ["lead-round-robin-assignees"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed to update manager"),
  });

  const setDeniedSourcesMutation = useMutation({
    mutationFn: ({ staffId, deniedSources }: { staffId: string; deniedSources: string[] }) =>
      hospitalAdminApi.setRoundRobinAssigneeDeniedSources(staffId, deniedSources),
    onSuccess: async () => {
      toast.success("Lead source allocation updated");
      setSourcesDialogFor(null);
      await qc.invalidateQueries({ queryKey: ["lead-round-robin-assignees"] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message || err?.message || "Failed to update sources"),
  });

  const markExitedMutation = useMutation({
    mutationFn: ({ staffId, managerId }: { staffId: string; managerId: string | null }) =>
      hospitalAdminApi.markRoundRobinAssigneeExited(staffId, managerId),
    onSuccess: async (data) => {
      toast.success(`Salesperson marked exited. ${data?.reassignedLeads ?? 0} lead(s) moved to manager.`);
      await qc.invalidateQueries({ queryKey: ["lead-round-robin-assignees"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed to mark salesperson exited"),
  });

  const createKeyMutation = useMutation({
    mutationFn: (payload: { name: string; assignedToId: string }) =>
      hospitalAdminApi.createAgencyApiKey(payload),
    onSuccess: async (row) => {
      toast.success("Agency API key created");
      if (row?.apiKey) {
        window.prompt("Copy this API key now (it won't be shown again):", row.apiKey);
      }
      setKeyName("");
      setOwnerId("");
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["lead-agency-keys"] }),
        qc.invalidateQueries({ queryKey: ["lead-agency-users"] }),
      ]);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed to create key"),
  });

  const updateKeyMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: { name: string; assignedToId: string };
    }) => hospitalAdminApi.updateAgencyApiKey(id, payload),
    onSuccess: async () => {
      toast.success("Agency key updated");
      setEditingKey(null);
      await qc.invalidateQueries({ queryKey: ["lead-agency-keys"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed to update key"),
  });

  const setStatusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      hospitalAdminApi.setAgencyApiKeyStatus(id, isActive),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["lead-agency-keys"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed to update key"),
  });

  const copyIngestKeyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      toast.success("Ingest key ID copied");
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  const rotateMutation = useMutation({
    mutationFn: (id: string) => hospitalAdminApi.rotateAgencyApiKey(id),
    onSuccess: async (row) => {
      toast.success("Agency API key rotated");
      if (row?.apiKey) {
        window.prompt("Copy the rotated API key now (it won't be shown again):", row.apiKey);
      }
      await qc.invalidateQueries({ queryKey: ["lead-agency-keys"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed to rotate key"),
  });

  const createAutoReplyRuleMutation = useMutation({
    mutationFn: (payload: { name: string; keywords: string[]; replyText: string; priority: number }) =>
      hospitalAdminApi.createWhatsAppAutoReplyRule(payload),
    onSuccess: async () => {
      toast.success("Auto-reply rule created");
      setRuleName("");
      setRuleKeywords("");
      setRuleReplyText("");
      setRulePriority("100");
      await qc.invalidateQueries({ queryKey: ["whatsapp-auto-reply-rules"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed to create rule"),
  });

  const updateAutoReplyRuleMutation = useMutation({
    mutationFn: ({ id, payload }: {
      id: string;
      payload: Partial<{
        name: string;
        keywords: string[];
        replyText: string;
        priority: number;
        isActive: boolean;
      }>;
    }) => hospitalAdminApi.updateWhatsAppAutoReplyRule(id, payload),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["whatsapp-auto-reply-rules"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed to update rule"),
  });

  const deleteAutoReplyRuleMutation = useMutation({
    mutationFn: (id: string) => hospitalAdminApi.deleteWhatsAppAutoReplyRule(id),
    onSuccess: async () => {
      toast.success("Auto-reply rule deleted");
      await qc.invalidateQueries({ queryKey: ["whatsapp-auto-reply-rules"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed to delete rule"),
  });
  const seedDefaultRulesMutation = useMutation({
    mutationFn: () => hospitalAdminApi.seedDefaultWhatsAppAutoReplyRules(),
    onSuccess: async (data) => {
      toast.success(`Default rules seeded (created: ${data.created}, skipped: ${data.skipped})`);
      await qc.invalidateQueries({ queryKey: ["whatsapp-auto-reply-rules"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed to seed defaults"),
  });

  const createWhatsAppNumberMutation = useMutation({
    mutationFn: (payload: CreateHospitalWhatsAppPayload) =>
      hospitalAdminApi.createHospitalWhatsAppNumber(payload),
    onSuccess: async () => {
      toast.success("WhatsApp number saved");
      setWaPhoneNumberId("");
      setWaAccessToken("");
      setWaWabaId("");
      setWaName("");
      setWaIsDefault(false);
      setWaIsActive(true);
      await qc.invalidateQueries({ queryKey: ["hospital-whatsapp-numbers"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed to save WhatsApp number"),
  });

  const updateWhatsAppNumberMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateHospitalWhatsAppPayload }) =>
      hospitalAdminApi.updateHospitalWhatsAppNumber(id, payload),
    onSuccess: async () => {
      toast.success("WhatsApp number updated");
      await qc.invalidateQueries({ queryKey: ["hospital-whatsapp-numbers"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed to update WhatsApp number"),
  });

  const deleteWhatsAppNumberMutation = useMutation({
    mutationFn: (id: string) => hospitalAdminApi.deleteHospitalWhatsAppNumber(id),
    onSuccess: async () => {
      toast.success("WhatsApp number deleted");
      await qc.invalidateQueries({ queryKey: ["hospital-whatsapp-numbers"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed to delete WhatsApp number"),
  });

  const editWhatsAppNumber = (row: HospitalWhatsAppNumber) => {
    const nextName = window.prompt("Display name (optional)", row.name ?? "");
    if (nextName === null) return;
    const keyOptions = apiKeys.filter((k) => k.isActive).map((k) => k.name).join(", ");
    const currentKeyName = row.leadIngestApiKey?.name ?? "";
    const nextKeyName = window.prompt(
      `CRM ingest key name (blank = auto). Options: ${keyOptions}`,
      currentKeyName
    );
    if (nextKeyName === null) return;
    const matchedKey = nextKeyName.trim()
      ? apiKeys.find((k) => k.name.toLowerCase() === nextKeyName.trim().toLowerCase())
      : null;
    if (nextKeyName.trim() && !matchedKey) {
      toast.error("Ingest key not found");
      return;
    }
    const nextWaba = window.prompt("WABA ID (optional)", row.wabaId ?? "");
    if (nextWaba === null) return;
    const nextToken = window.prompt("Access token (required)", "");
    if (nextToken === null) return;
    const nextIsDefault = window.confirm("Make this the default WhatsApp number for CRM?");
    updateWhatsAppNumberMutation.mutate({
      id: row.id,
      payload: {
        name: nextName.trim() ? nextName.trim() : null,
        wabaId: nextWaba.trim() ? nextWaba.trim() : null,
        leadIngestApiKeyId: matchedKey?.id ?? null,
        ...(nextToken.trim() ? { accessToken: nextToken.trim() } : {}),
        isDefault: nextIsDefault,
      },
    });
  };

  const parseKeywordsInput = (raw: string): string[] =>
    raw
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);

  const editAutoReplyRule = (rule: WhatsAppAutoReplyRule) => {
    const nextName = window.prompt("Rule name", rule.name);
    if (nextName === null) return;
    const nextKeywordsRaw = window.prompt(
      "Keywords (comma-separated)",
      Array.isArray(rule.keywords) ? rule.keywords.join(", ") : ""
    );
    if (nextKeywordsRaw === null) return;
    const nextReply = window.prompt("Auto-reply text", rule.replyText);
    if (nextReply === null) return;
    const nextPriorityRaw = window.prompt("Priority (lower runs first)", String(rule.priority));
    if (nextPriorityRaw === null) return;

    const nextKeywords = parseKeywordsInput(nextKeywordsRaw);
    const nextPriority = Number(nextPriorityRaw);
    if (!nextName.trim() || !nextReply.trim() || nextKeywords.length === 0 || !Number.isFinite(nextPriority)) {
      toast.error("Please provide valid rule name, keywords, reply, and priority");
      return;
    }

    updateAutoReplyRuleMutation.mutate({
      id: rule.id,
      payload: {
        name: nextName.trim(),
        keywords: nextKeywords,
        replyText: nextReply.trim(),
        priority: nextPriority,
      },
    });
  };

  return (
    <div className="p-6 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Mini CRM - Round Robin</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3">
            <Checkbox
              checked={enabled}
              onCheckedChange={(v) => {
                setConfigMutation.mutate(!!v);
              }}
              disabled={isLoading || setConfigMutation.isPending}
            />
            <div>
              <Label className="font-medium">Enable round robin lead assignment</Label>
              <div className="text-sm text-muted-foreground mt-1">
                When enabled, leads created via API ingest / admin creation (that are unassigned) will be distributed
                across active <span className="font-medium">SALES_PERSON</span> users in the hospital in a round-robin order.
              </div>
              {typeof data?.leadRoundRobinCursor === "number" && (
                <div className="text-xs text-muted-foreground mt-2">
                  Current cursor: {data.leadRoundRobinCursor}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => setConfigMutation.mutate(false)}
              disabled={isLoading || setConfigMutation.isPending || !enabled}
              className="ml-auto"
            >
              Disable
            </Button>
          </div>

          <div className="pt-3 border-t space-y-2">
            <Label className="font-medium">In-house sales persons</Label>
            <div className="text-sm text-muted-foreground">
              Turn OFF a salesperson when they’re on leave. This applies to <strong>every</strong> automatic
              lead source (Meta, Google, WhatsApp, Instagram, Exotel, Practo, landing pages, patient app,
              API ingest). Round robin only picks Available (ON) sales persons. By default all lead sources
              are allowed; use <strong>Sources</strong> to block specific sources for a person so those
              leads skip them. If an open lead is already owned by someone who is OFF or blocked for that
              source, the next inbound for that phone moves it to an Available person. Marking a salesperson
              exited moves all of their leads to the selected sales manager and removes them from active BD
              filters and future round robin assignment. Manual Assign in the leads screen can still override.
            </div>
            <div className="rounded-lg border bg-white overflow-x-auto">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-medium text-muted-foreground border-b min-w-[900px]">
                <div className="col-span-2">Name</div>
                <div className="col-span-2">Email</div>
                <div className="col-span-2">Reporting manager</div>
                <div className="col-span-2">Blocked sources</div>
                <div className="col-span-1 text-center">Status</div>
                <div className="col-span-1 text-right">Available</div>
                <div className="col-span-2 text-right">Exit</div>
              </div>
              {rrAssignees.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground">No in-house sales persons found.</div>
              ) : (
                rrAssignees.map((u) => {
                  const denied = u.leadDeniedSources ?? [];
                  return (
                  <div key={u.id} className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-b last:border-b-0 text-sm min-w-[900px]">
                    <div className="col-span-2 font-medium">{u.name}</div>
                    <div className="col-span-2 text-muted-foreground truncate">{u.email}</div>
                    <div className="col-span-2">
                      <Select
                        value={u.reportsToManagerId ?? "__none__"}
                        onValueChange={(value) =>
                          setAssigneeManagerMutation.mutate({
                            staffId: u.id,
                            managerId: value === "__none__" ? null : value,
                          })
                        }
                        disabled={setAssigneeManagerMutation.isPending || !!u.exitedAt}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select manager" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">No manager</SelectItem>
                          {rrManagers.map((manager) => (
                            <SelectItem key={manager.id} value={manager.id}>
                              {manager.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 w-full justify-start text-xs"
                        disabled={!!u.exitedAt || u.status === "INACTIVE"}
                        onClick={() => {
                          setSourcesDialogFor(u);
                          setDraftDeniedSources([...(u.leadDeniedSources ?? [])]);
                        }}
                      >
                        {denied.length === 0
                          ? "All sources"
                          : `${denied.length} blocked`}
                      </Button>
                    </div>
                    <div className="col-span-1 text-center">
                      <span className={`inline-flex rounded-full px-2 py-1 text-[11px] ${
                        u.exitedAt || u.status === "INACTIVE"
                          ? "bg-red-100 text-red-700"
                          : "bg-emerald-100 text-emerald-700"
                      }`}>
                        {u.exitedAt || u.status === "INACTIVE" ? "Exited" : "Active"}
                      </span>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Checkbox
                        checked={!!u.isLeadRoundRobinActive}
                        disabled={!enabled || setAssigneeMutation.isPending || !!u.exitedAt || u.status === "INACTIVE"}
                        onCheckedChange={(v) => setAssigneeMutation.mutate({ staffId: u.id, isActive: !!v })}
                      />
                    </div>
                    <div className="col-span-2 flex justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={
                          !!u.exitedAt ||
                          u.status === "INACTIVE" ||
                          markExitedMutation.isPending ||
                          !u.reportsToManagerId
                        }
                        onClick={() => {
                          if (!u.reportsToManagerId) {
                            toast.error("Select a reporting manager first");
                            return;
                          }
                          if (window.confirm(`Mark ${u.name} as exited and move all assigned leads to the selected sales manager?`)) {
                            markExitedMutation.mutate({ staffId: u.id, managerId: u.reportsToManagerId });
                          }
                        }}
                      >
                        Exit
                      </Button>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
            {!enabled && (
              <div className="text-xs text-muted-foreground">
                Enable round robin to use these toggles.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>WhatsApp Inbound Auto-Reply Rules</CardTitle>
          <p className="text-sm text-muted-foreground font-normal mt-1">
            Define hospital-specific keyword rules so inbound WhatsApp messages get contextual automatic responses.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Rule name</Label>
              <Input
                value={ruleName}
                onChange={(e) => setRuleName(e.target.value)}
                placeholder="e.g. Rhinoplasty enquiry"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs text-muted-foreground">Keywords (comma-separated)</Label>
              <Input
                value={ruleKeywords}
                onChange={(e) => setRuleKeywords(e.target.value)}
                placeholder="rhinoplasty, nose job, rhino"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Priority (lower first)</Label>
              <Input
                type="number"
                min={1}
                value={rulePriority}
                onChange={(e) => setRulePriority(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Auto-reply message</Label>
            <Textarea
              value={ruleReplyText}
              onChange={(e) => setRuleReplyText(e.target.value)}
              placeholder="Thank you for your interest in Rhinoplasty..."
              rows={4}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => seedDefaultRulesMutation.mutate()}
              disabled={seedDefaultRulesMutation.isPending}
            >
              {seedDefaultRulesMutation.isPending ? "Seeding..." : "Seed default surgery rules"}
            </Button>
            <Button
              onClick={() =>
                createAutoReplyRuleMutation.mutate({
                  name: ruleName.trim(),
                  keywords: parseKeywordsInput(ruleKeywords),
                  replyText: ruleReplyText.trim(),
                  priority: Number(rulePriority || 100),
                })
              }
              disabled={
                createAutoReplyRuleMutation.isPending ||
                !ruleName.trim() ||
                parseKeywordsInput(ruleKeywords).length === 0 ||
                !ruleReplyText.trim()
              }
            >
              {createAutoReplyRuleMutation.isPending ? "Creating..." : "Add rule"}
            </Button>
          </div>

          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-left p-2 font-medium">Priority</th>
                  <th className="text-left p-2 font-medium">Rule</th>
                  <th className="text-left p-2 font-medium">Keywords</th>
                  <th className="text-left p-2 font-medium">Reply preview</th>
                  <th className="text-left p-2 font-medium">Status</th>
                  <th className="text-right p-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingAutoReplyRules ? (
                  <tr><td colSpan={6} className="p-3 text-muted-foreground">Loading auto-reply rules...</td></tr>
                ) : autoReplyRules.length === 0 ? (
                  <tr><td colSpan={6} className="p-3 text-muted-foreground">No rules configured yet.</td></tr>
                ) : (
                  autoReplyRules.map((rule) => (
                    <tr key={rule.id} className="border-t align-top">
                      <td className="p-2">{rule.priority}</td>
                      <td className="p-2 font-medium">{rule.name}</td>
                      <td className="p-2 text-xs text-muted-foreground max-w-xs break-words">
                        {Array.isArray(rule.keywords) ? rule.keywords.join(", ") : ""}
                      </td>
                      <td className="p-2 text-xs text-muted-foreground max-w-sm break-words">
                        {rule.replyText}
                      </td>
                      <td className="p-2">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${rule.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                          {rule.isActive ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>
                      <td className="p-2 text-right">
                        <div className="inline-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => editAutoReplyRule(rule)}
                            disabled={updateAutoReplyRuleMutation.isPending}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateAutoReplyRuleMutation.mutate({
                                id: rule.id,
                                payload: { isActive: !rule.isActive },
                              })
                            }
                            disabled={updateAutoReplyRuleMutation.isPending}
                          >
                            {rule.isActive ? "Disable" : "Enable"}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteAutoReplyRuleMutation.mutate(rule.id)}
                            disabled={deleteAutoReplyRuleMutation.isPending}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>WhatsApp Numbers (Multi-number)</CardTitle>
          <p className="text-sm text-muted-foreground font-normal mt-1">
            Add all WhatsApp Cloud API phone numbers. Assign a <strong>CRM ingest key</strong> per line so one Meta webhook routes leads correctly (landing Rhinoplasty, Google Paid, Insta, etc.) without changing the webhook URL.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Phone number ID (phone_number_id)</Label>
              <Input
                value={waPhoneNumberId}
                onChange={(e) => setWaPhoneNumberId(e.target.value)}
                placeholder="e.g. 110889268958350"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Access token</Label>
              <Input
                value={waAccessToken}
                onChange={(e) => setWaAccessToken(e.target.value)}
                placeholder="Permanent/system token"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">WABA ID (optional)</Label>
              <Input value={waWabaId} onChange={(e) => setWaWabaId(e.target.value)} placeholder="e.g. 9485..." />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Name (optional)</Label>
              <Input value={waName} onChange={(e) => setWaName(e.target.value)} placeholder="e.g. Google Paid" />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs text-muted-foreground">CRM ingest key (leads from this line)</Label>
              <Select value={waLeadIngestKeyId || "__none__"} onValueChange={(v) => setWaLeadIngestKeyId(v === "__none__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Select ingest key" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Auto from line name —</SelectItem>
                  {apiKeys.filter((k) => k.isActive).map((k) => (
                    <SelectItem key={k.id} value={k.id}>{k.name} ({k.keyPrefix})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={waIsActive} onCheckedChange={(v) => setWaIsActive(v === true)} />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={waIsDefault} onCheckedChange={(v) => setWaIsDefault(v === true)} />
              Default number for CRM
            </label>
            <div className="flex-1" />
            <Button
              onClick={() =>
                createWhatsAppNumberMutation.mutate({
                  phoneNumberId: waPhoneNumberId.trim(),
                  accessToken: waAccessToken.trim(),
                  wabaId: waWabaId.trim() ? waWabaId.trim() : null,
                  name: waName.trim() ? waName.trim() : null,
                  leadIngestApiKeyId: waLeadIngestKeyId.trim() ? waLeadIngestKeyId.trim() : null,
                  isActive: waIsActive,
                  isDefault: waIsDefault,
                })
              }
              disabled={
                createWhatsAppNumberMutation.isPending ||
                !waPhoneNumberId.trim() ||
                !waAccessToken.trim()
              }
            >
              {createWhatsAppNumberMutation.isPending ? "Saving..." : "Add number"}
            </Button>
          </div>

          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-left p-2 font-medium">Name</th>
                  <th className="text-left p-2 font-medium">Phone number ID</th>
                  <th className="text-left p-2 font-medium">Token</th>
                  <th className="text-left p-2 font-medium">CRM ingest key</th>
                  <th className="text-left p-2 font-medium">Default</th>
                  <th className="text-left p-2 font-medium">Status</th>
                  <th className="text-right p-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingWhatsAppNumbers ? (
                  <tr><td colSpan={7} className="p-3 text-muted-foreground">Loading WhatsApp numbers...</td></tr>
                ) : hospitalWhatsAppNumbers.length === 0 ? (
                  <tr><td colSpan={7} className="p-3 text-muted-foreground">No WhatsApp numbers configured yet.</td></tr>
                ) : (
                  hospitalWhatsAppNumbers.map((r) => (
                    <tr key={r.id} className="border-t align-top">
                      <td className="p-2">{r.name ?? "—"}</td>
                      <td className="p-2 font-mono text-xs">{r.phoneNumberId}</td>
                      <td className="p-2 font-mono text-xs">{r.accessTokenMasked || "—"}</td>
                      <td className="p-2 text-xs">{r.leadIngestApiKey?.name ?? "— auto —"}</td>
                      <td className="p-2">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${r.isDefault ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                          {r.isDefault ? "YES" : "NO"}
                        </span>
                      </td>
                      <td className="p-2">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${r.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                          {r.isActive ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>
                      <td className="p-2 text-right">
                        <div className="inline-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => editWhatsAppNumber(r)}
                            disabled={updateWhatsAppNumberMutation.isPending}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateWhatsAppNumberMutation.mutate({
                                id: r.id,
                                payload: { isActive: !r.isActive },
                              })
                            }
                            disabled={updateWhatsAppNumberMutation.isPending}
                          >
                            {r.isActive ? "Disable" : "Enable"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateWhatsAppNumberMutation.mutate({
                                id: r.id,
                                payload: { isDefault: true },
                              })
                            }
                            disabled={updateWhatsAppNumberMutation.isPending}
                          >
                            Make default
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteWhatsAppNumberMutation.mutate(r.id)}
                            disabled={deleteWhatsAppNumberMutation.isPending}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={!!editingKey}
        onOpenChange={(open) => {
          if (!open) setEditingKey(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit agency key</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Agency key name</Label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="e.g. Meta Agency Key"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Assign to sales user</Label>
              <Select value={editOwnerId} onValueChange={setEditOwnerId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select SALES_PERSON user" />
                </SelectTrigger>
                <SelectContent>
                  {editOwnerOptions.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditingKey(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={
                updateKeyMutation.isPending ||
                !editName.trim() ||
                !editOwnerId
              }
              onClick={() => {
                if (!editingKey) return;
                updateKeyMutation.mutate({
                  id: editingKey.id,
                  payload: { name: editName.trim(), assignedToId: editOwnerId },
                });
              }}
            >
              {updateKeyMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>Agency API Keys (Ingest)</CardTitle>
          <p className="text-sm text-muted-foreground font-normal mt-1">
            The sales user you assign here is only the <strong>owner</strong> for ingested leads — their login stays full
            access. For an external partner who should only <strong>view</strong> leads (read-only), create a separate
            sales user in User Management and enable &quot;External agency partner (read-only CRM)&quot;.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Agency key name</Label>
              <Input
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                placeholder="e.g. Meta Agency Key"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Assign to sales user</Label>
              <Select value={ownerId} onValueChange={setOwnerId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select SALES_PERSON user" />
                </SelectTrigger>
                <SelectContent>
                  {ownerOptions.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={() => createKeyMutation.mutate({ name: keyName.trim(), assignedToId: ownerId })}
                disabled={createKeyMutation.isPending || !keyName.trim() || !ownerId}
              >
                {createKeyMutation.isPending ? "Creating..." : "Create key"}
              </Button>
            </div>
          </div>

          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-left p-2 font-medium">Name</th>
                  <th className="text-left p-2 font-medium">Owner</th>
                  <th className="text-left p-2 font-medium min-w-[14rem]">Ingest key ID (webhooks)</th>
                  <th className="text-left p-2 font-medium">Key prefix</th>
                  <th className="text-left p-2 font-medium">Last used</th>
                  <th className="text-left p-2 font-medium">Status</th>
                  <th className="text-right p-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingKeys ? (
                  <tr><td colSpan={7} className="p-3 text-muted-foreground">Loading keys...</td></tr>
                ) : apiKeys.length === 0 ? (
                  <tr><td colSpan={7} className="p-3 text-muted-foreground">No agency keys yet.</td></tr>
                ) : (
                  apiKeys.map((k) => (
                    <tr key={k.id} className="border-t">
                      <td className="p-2">{k.name}</td>
                      <td className="p-2">{k.assignedTo?.name ?? k.assignedToId}</td>
                      <td className="p-2 align-top">
                        <div className="flex items-start gap-2 max-w-xs sm:max-w-md">
                          <span className="font-mono text-xs break-all text-muted-foreground">{k.id}</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 shrink-0 p-0"
                            onClick={() => copyIngestKeyId(k.id)}
                            title="Copy ingest key ID"
                            aria-label="Copy ingest key ID"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                      <td className="p-2 font-mono">{k.keyPrefix}</td>
                      <td className="p-2">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString("en-IN") : "Never"}</td>
                      <td className="p-2">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${k.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                          {k.isActive ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>
                      <td className="p-2 text-right">
                        <div className="inline-flex flex-wrap justify-end gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setEditingKey(k);
                              setEditName(k.name);
                              setEditOwnerId(k.assignedToId);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setStatusMutation.mutate({ id: k.id, isActive: !k.isActive })}
                            disabled={setStatusMutation.isPending}
                          >
                            {k.isActive ? "Disable" : "Enable"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => rotateMutation.mutate(k.id)}
                            disabled={rotateMutation.isPending}
                          >
                            Rotate key
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sarvam Voice AI</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Outbound voice agent, disposition mapping, billing, and call logs are on the Sarvam integrations page.
          </p>
          <Button variant="outline" asChild>
            <Link to="/hospital-admin/integrations/sarvam">
              Open Sarvam integrations
              <ExternalLink className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>

      <Dialog
        open={!!sourcesDialogFor}
        onOpenChange={(open) => {
          if (!open) setSourcesDialogFor(null);
        }}
      >
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Lead sources — {sourcesDialogFor?.name ?? ""}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            By default all sources are allowed. Uncheck a source to block it — new automatic leads from
            that source will not be assigned to this salesperson.
          </p>
          <div className="space-y-2 py-2">
            {CRM_LEAD_SOURCES.map((s) => {
              const blocked = draftDeniedSources.includes(s.value);
              return (
                <label
                  key={s.value}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                >
                  <Checkbox
                    checked={!blocked}
                    onCheckedChange={(v) => {
                      const allow = v === true;
                      setDraftDeniedSources((prev) => {
                        if (allow) return prev.filter((x) => x !== s.value);
                        return prev.includes(s.value) ? prev : [...prev, s.value];
                      });
                    }}
                  />
                  <span>{s.label}</span>
                  <span className="text-xs text-muted-foreground font-mono">{s.value}</span>
                </label>
              );
            })}
          </div>
          {draftDeniedSources.some(
            (d) => !CRM_LEAD_SOURCES.some((s) => s.value === d)
          ) && (
            <div className="text-xs text-muted-foreground border-t pt-2">
              Also blocked (custom):{" "}
              {draftDeniedSources
                .filter((d) => !CRM_LEAD_SOURCES.some((s) => s.value === d))
                .map((d) => formatLeadSourceLabel(d))
                .join(", ")}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDraftDeniedSources([])}
              disabled={setDeniedSourcesMutation.isPending}
            >
              Allow all
            </Button>
            <Button type="button" variant="outline" onClick={() => setSourcesDialogFor(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={setDeniedSourcesMutation.isPending || !sourcesDialogFor}
              onClick={() => {
                if (!sourcesDialogFor) return;
                setDeniedSourcesMutation.mutate({
                  staffId: sourcesDialogFor.id,
                  deniedSources: draftDeniedSources,
                });
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

